import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import questionsConfig from './config/questions.json';
import { useLanguage } from './contexts/LanguageContext';
import MobileKeyboard from './components/MobileKeyboard';
import { VALIDATION_CONFIG } from './constants/constant';

/**
 * Converts a number to the appropriate numeral system based on language
 * @param {number} num - The number to convert
 * @param {string} lang - The language code ('en', 'mr', 'hi')
 * @returns {string} - The number in the appropriate numeral system
 */
const convertToLocalNumerals = (num, lang) => {
  if (lang === 'en') {
    return num.toString();
  }

  const devanagariNumerals = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
  return num.toString().split('').map(digit => devanagariNumerals[parseInt(digit)]).join('');
};

function Feedback() {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [displayQuestion, setDisplayQuestion] = useState(1);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState('next');
  const { language, translations } = useLanguage();
  const questions = questionsConfig.questions;
  const totalQuestions = questions.length;

  const currentQuestionData = questions[displayQuestion - 1];

  const isCurrentQuestionValid = () => {
    const question = currentQuestionData;
    let answer = answers[question.id];

    // Optional question + empty answer → valid
    if (!question.required && !answer) {
      if (displayQuestion === 1) {
        return isAnonymous;
      }
      return true;
    }

    // RADIO
    if (question.type === 'radio') {
      return answer !== undefined && answer !== null;
    }

    // CHECKBOX
    if (question.type === 'checkbox') {
      return Array.isArray(answer) && answer.length > 0;
    }

    // TEXT / TEXTAREA
    if (question.type === 'text' || question.type === 'textarea') {
      if (!answer) return false;

      // Trim whitespace - treat whitespace-only as empty
      const trimmedAnswer = typeof answer === 'string' ? answer.trim() : answer;

      // If answer is only whitespace, treat as empty
      if (trimmedAnswer === '') {
        // Optional questions allow empty/whitespace-only
        if (!question.required) return true;
        // Required questions don't allow whitespace-only
        return false;
      }

      //TODO: Uncomment this when we have a way to sanitize the input(proper regex for each language)
      // answer = sanitizeInput(trimmedAnswer);
      const rule = VALIDATION_CONFIG[question.id];

      // If no rule exists, use default validation
      if (!rule) {
        return trimmedAnswer.length >= 2 && trimmedAnswer.length <= 1000;
      }

      // NAME
      if (rule?.type === 'name') {
        if (question.isAnonymousSelected && !trimmedAnswer) return true;
        if (trimmedAnswer.length < rule.min || trimmedAnswer.length > rule.max) return false;
        return rule.regex.some(regex => regex.test(trimmedAnswer));

      }

      // CITY
      if (rule?.type === 'city') {
        if (trimmedAnswer.length < rule.min || trimmedAnswer.length > rule.max) return false;
        return rule.regex.some(regex => regex.test(trimmedAnswer));
      }

      // CONTACT
      if (rule?.type === 'contact') {
        if (!trimmedAnswer) return true;
        const isValidContact = rule.regex.some(regex => regex.test(trimmedAnswer));
        return isValidContact;
      }

      // RULE WITH MIN/MAX BUT NO TYPE (questions 8, 9, 10, 11, 12, 13)
      if (rule.min !== undefined && rule.max !== undefined) {
        return trimmedAnswer.length >= rule.min && trimmedAnswer.length <= rule.max;
      }

      // DEFAULT TEXT RULE (FALLBACK)
      return trimmedAnswer.length >= 2 && trimmedAnswer.length <= 1000;
    }

    return true;
  };

  const handleNext = () => {
    if (currentQuestion < totalQuestions) {
      const nextQuestion = currentQuestion + 1;
      const nextQuestionData = questions[nextQuestion - 1];
      const isNextQuestionTextInput = nextQuestionData.type === 'text' || nextQuestionData.type === 'textarea';

      setTransitionDirection('next');
      setIsTransitioning(true);

      // Only close keyboard if next question is not a text/textarea input
      if (!isNextQuestionTextInput) {
        setShowKeyboard(false);
      }

      // Exit animation (300ms)
      setTimeout(() => {
        // Update display question after exit animation
        setCurrentQuestion(nextQuestion);
        setDisplayQuestion(nextQuestion);

        // Start enter animation on next frame
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsTransitioning(false);
          });
        });
      }, 300);
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 1) {
      const prevQuestion = currentQuestion - 1;
      setTransitionDirection('prev');
      setIsTransitioning(true);
      setShowKeyboard(false);

      // Exit animation (300ms)
      setTimeout(() => {
        // Update display question after exit animation
        setCurrentQuestion(prevQuestion);
        setDisplayQuestion(prevQuestion);

        // Start enter animation on next frame
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsTransitioning(false);
          });
        });
      }, 300);
    }
  };

  const handleRadioChange = (optionId) => {
    setAnswers({
      ...answers,
      [displayQuestion]: optionId
    });
  };

  const handleCheckboxChange = (optionId) => {
    const currentAnswers = answers[displayQuestion] || [];
    const newAnswers = currentAnswers.includes(optionId)
      ? currentAnswers.filter(id => id !== optionId)
      : [...currentAnswers, optionId];

    setAnswers({
      ...answers,
      [displayQuestion]: newAnswers
    });
  };

  const validateRequiredQuestions = () => {
    const missingQuestions = [];

    questions.forEach((question, index) => {
      if (question.required === true) {
        const questionNumber = index + 1;
        const answer = answers[questionNumber];

        if (question.type === 'text' || question.type === 'textarea') {
          if (!answer || answer.trim() === '') {
            missingQuestions.push(questionNumber);
          }
        } else if (question.type === 'radio') {
          if (!answer) {
            missingQuestions.push(questionNumber);
          }
        } else if (question.type === 'checkbox') {
          if (!answer || (Array.isArray(answer) && answer.length === 0)) {
            missingQuestions.push(questionNumber);
          }
        }
      }
    });

    return missingQuestions;
  };

  const formatSubmissionData = () => {
    const submissionData = {
      data: {}
    };

    questions.forEach((question, index) => {
      const questionNumber = index + 1;
      const questionText = question[language].text;

      // If anonymous is checked and this is question 1 (name), set to null
      if (questionNumber === 1 && isAnonymous) {
        submissionData.data[questionText] = '';
        return;
      }

      const answer = answers[questionNumber];

      if (question.type === 'text' || question.type === 'textarea') {
        // For text inputs and textareas, use the text value directly or blank string if empty
        submissionData.data[questionText] = (answer !== undefined && answer !== null && answer !== '') ? answer : '';
      } else if (question.type === 'radio') {
        // For radio buttons, find the option by ID and get its text, or blank string if empty
        if (answer !== undefined && answer !== null && answer !== '') {
          const selectedOption = question.options.find(opt => opt.id === answer);
          submissionData.data[questionText] = selectedOption ? selectedOption[language].text : '';
        } else {
          submissionData.data[questionText] = '';
        }
      } else if (question.type === 'checkbox') {
        // For checkboxes, convert array of IDs to array of texts, or blank string if empty
        if (Array.isArray(answer) && answer.length > 0) {
          submissionData.data[questionText] = answer
            .map(optionId => {
              const selectedOption = question.options.find(opt => opt.id === optionId);
              return selectedOption ? selectedOption[language].text : null;
            })
            .filter(text => text !== null);
        } else {
          submissionData.data[questionText] = '';
        }
      } else {
        // Default case: set to blank string
        submissionData.data[questionText] = '';
      }
    });

    return submissionData;
  };

  /**
   * Saves submission data to localStorage when offline
   * @param {Object} submissionData - The formatted submission data
   */
  const saveToLocalStorage = (submissionData) => {
    try {
      const pendingSubmissions = JSON.parse(localStorage.getItem('pendingFeedbackSubmissions') || '[]');
      const submissionWithTimestamp = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        data: submissionData,
        timestamp: new Date().toISOString()
      };
      pendingSubmissions.push(submissionWithTimestamp);
      localStorage.setItem('pendingFeedbackSubmissions', JSON.stringify(pendingSubmissions));
      return true;
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      return false;
    }
  };

  /**
   * Submits a single feedback submission to the API
   * @param {Object} submissionData - The submission data to send
   * @returns {Promise<boolean>} - Returns true if successful, false otherwise
   */
  const submitFeedback = async (submissionData) => {
    try {
      // const response = await fetch('http://192.168.1.151:8800/v1/feedback/create', {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/feedback/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      return true;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      return false;
    }
  };

  /**
   * Processes all pending submissions from localStorage when online
   */
  const processPendingSubmissions = async () => {
    if (!navigator.onLine || isProcessingPendingRef.current) {
      return;
    }

    isProcessingPendingRef.current = true;

    try {
      const pendingSubmissions = JSON.parse(localStorage.getItem('pendingFeedbackSubmissions') || '[]');

      if (pendingSubmissions.length === 0) {
        return;
      }

      const successfulSubmissionIds = [];

      for (const pendingSubmission of pendingSubmissions) {
        const success = await submitFeedback(pendingSubmission.data);

        if (success) {
          successfulSubmissionIds.push(pendingSubmission.id);
        }
      }

      // Remove successfully submitted items by ID
      if (successfulSubmissionIds.length > 0) {
        const remainingSubmissions = pendingSubmissions.filter(
          (submission) => !successfulSubmissionIds.includes(submission.id)
        );
        localStorage.setItem('pendingFeedbackSubmissions', JSON.stringify(remainingSubmissions));
      }
    } catch (error) {
      console.error('Error processing pending submissions:', error);
    } finally {
      isProcessingPendingRef.current = false;
    }
  };

  const handleSubmit = async () => {
    const missingQuestions = validateRequiredQuestions();

    if (missingQuestions.length > 0) {
      const firstMissingQuestion = missingQuestions[0];
      setCurrentQuestion(firstMissingQuestion);
      setDisplayQuestion(firstMissingQuestion);
      return;
    }

    setIsSubmitting(true);

    try {
      const submissionData = formatSubmissionData();

      // Check if user is online
      if (!navigator.onLine) {
        // Save to localStorage when offline
        const saved = saveToLocalStorage(submissionData);
        if (saved) {
          navigate('/thank-you');
        }
        setIsSubmitting(false);
        return;
      }

      // User is online, submit directly
      const success = await submitFeedback(submissionData);

      if (!success) {
        throw new Error('Failed to submit feedback');
      }

      // Navigate to thank you page
      navigate('/thank-you');
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const [showKeyboard, setShowKeyboard] = useState(false);
  const keyboardWrapperRef = useRef(null);
  const inputRef = useRef(null);
  const isProcessingPendingRef = useRef(false);

  // Detect if device is touch-enabled (mobile/tablet)
  const isTouchDevice = 'ontouchstart' in window || 
    navigator.maxTouchPoints > 0 || 
    navigator.msMaxTouchPoints > 0;

  const handleKeyboardInput = (input) => {
    setAnswers({ ...answers, [displayQuestion]: input });
  };

  const handleInputFocus = () => {
    // Only show custom keyboard on touch devices
    if (isTouchDevice) {
      setShowKeyboard(true);
    }
  };

  // Handle clicks outside input and keyboard to hide keyboard
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!showKeyboard) return;

      const target = event.target;
      const inputElement = inputRef.current?.querySelector('input, textarea');
      const isClickOnInput = inputRef.current && (
        inputRef.current.contains(target) ||
        (inputElement && (inputElement === target || inputElement.contains(target)))
      );
      const isClickOnKeyboard = keyboardWrapperRef.current && keyboardWrapperRef.current.contains(target);

      // Hide keyboard only if clicking outside both input and keyboard
      if (!isClickOnInput && !isClickOnKeyboard) {
        setShowKeyboard(false);
      }
    };

    // Use mousedown instead of click to catch the event before blur
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showKeyboard]);

  const handleInputBlur = (e) => { };

  // Sync displayQuestion with currentQuestion when not transitioning
  useEffect(() => {
    if (!isTransitioning) {
      setDisplayQuestion(currentQuestion);
    }
  }, [currentQuestion, isTransitioning]);

  // Process pending submissions on component mount if online
  useEffect(() => {
    if (navigator.onLine) {
      processPendingSubmissions();
    }
  }, []);

  // Listen for online event and process pending submissions
  useEffect(() => {
    const handleOnline = () => {
      processPendingSubmissions();
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  const isTextInput = currentQuestionData.type === 'text' || currentQuestionData.type === 'textarea';

  // Auto-focus input/textarea when navigating to next question (skip question 1)
  useEffect(() => {
    if (!isTransitioning && isTextInput && inputRef.current && displayQuestion !== 1) {
      const inputElement = inputRef.current.querySelector('input, textarea');
      if (inputElement) {
        // Small delay to ensure DOM is ready after transition
        setTimeout(() => {
          inputElement.focus();
        }, 50);
      }
    }
  }, [displayQuestion, isTransitioning, isTextInput]);
  const currentInputValue = answers[displayQuestion] || '';
  const maxLength = VALIDATION_CONFIG[currentQuestionData.id]?.max || 1000;

  return (
    <div className="bg-beige-light min-h-dvh flex flex-col xl:flex-row">
      {/* Left Sidebar */}

      <div className="font-artifex w-full xl:w-[450px] bg-beige-medium px-4 sm:px-8 lg:px-[100px] xl:px-10 pt-24 sm:pt-[15%] xl:pt-[9%] pb-6 md:pb-16 xl:pb-0 flex flex-col relative">
        <div className="absolute top-6 sm:top-[15%] xl:top-10 left-4 sm:left-8 lg:left-[100px] xl:left-10">
          <button
            className="bg-transparent border-none cursor-pointer w-9 md:w-12 text-brown-dark flex items-center justify-center transition-opacity duration-200 hover:opacity-70"
            onClick={() => navigate('/')}
            aria-label={translations.labels.goHome}
          >
            <svg className='w-full h-full' width="88" height="79" viewBox="0 0 88 79" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M87.064 30.9834L45.3509 0.441675C44.5465 -0.147225 43.4537 -0.147225 42.6497 0.441675L0.936001 30.9834C-0.0828986 31.7295 -0.3043 33.1602 0.4418 34.179C1.1879 35.1979 2.6188 35.4189 3.6375 34.6732L44.0001 5.12028L84.363 34.673C84.77 34.9712 85.243 35.1147 85.712 35.1147C86.416 35.1147 87.11 34.7907 87.558 34.1789C88.304 33.1602 88.083 31.7295 87.064 30.9834Z" fill="#89371D" />
              <path d="M76.011 35.1133C74.748 35.1133 73.725 36.1369 73.725 37.3997V73.7939H55.4332V53.9277C55.4332 47.6234 50.3041 42.4946 44.0001 42.4946C37.6961 42.4946 32.567 47.6237 32.567 53.9277V73.7939H14.2755V37.3999C14.2755 36.1371 13.2517 35.1134 11.9891 35.1134C10.7265 35.1134 9.70258 36.1371 9.70258 37.3999V76.0809C9.70258 77.3439 10.7265 78.3669 11.9891 78.3669H34.8534C36.0559 78.3669 37.0399 77.4379 37.131 76.2589C37.1365 76.2059 37.1399 76.1469 37.1399 76.0809V53.9279C37.1399 50.1451 40.2173 47.0677 44.0001 47.0677C47.7829 47.0677 50.8603 50.1453 50.8603 53.9279V76.0809C50.8603 76.1469 50.8638 76.2039 50.8693 76.2569C50.9593 77.4369 51.9437 78.3669 53.1468 78.3669H76.011C77.274 78.3669 78.298 77.3439 78.298 76.0809V37.3999C78.297 36.1369 77.274 35.1133 76.011 35.1133Z" fill="#89371D" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:flex xl:flex-col gap-8 xl:gap-16">
          <div className={`flex max-sm:flex-row max-xl:flex-col items-center sm:items-start xl:items-center gap-4 md:gap-6 relative ${currentQuestionData.required === false ? 'active' : ''} after:bg-brown-dark after:content-[''] after:absolute after:left-[18px] sm:after:left-[43px] xl:after:left-[20px] after:top-9 sm:after:top-[21px] xl:after:top-[42px] after:w-0.5 sm:after:w-[calc(100%-12px)] xl:after:w-0.5 after:h-8 sm:after:h-0.5 xl:after:h-16`}>
            <div className={`border-4 border-beige-medium outline outline-1 w-9 sm:w-[42px] h-9 sm:h-[42px] rounded-full flex items-center justify-center text-lg font-forum flex-shrink-0 transition-all duration-300 ${currentQuestionData.required === false ? 'bg-brown-dark outline-brown-dark text-white' : 'bg-brown-dark/50 outline-brown-dark/50 text-white/50'}`}>
              1
            </div>
            <div className={`text-[22px] md:text-[30px] font-semibold leading-[28px] md:leading-[38px] mt-0.5 ${currentQuestionData.required === false ? 'text-brown-dark' : 'text-brown-dark/50'}`}>
              {translations.sidebar.section1}
            </div>
          </div>

          <div className={`flex max-sm:flex-row max-xl:flex-col items-center sm:items-start xl:items-center gap-4 md:gap-6 relative ${currentQuestionData.required === true ? 'active' : ''}`}>
            <div className={`border-4 border-beige-medium outline outline-1 w-9 sm:w-[42px] h-9 sm:h-[42px] rounded-full flex items-center justify-center text-lg font-forum flex-shrink-0 transition-all duration-300 ${currentQuestionData.required === true ? 'bg-brown-dark outline-brown-dark text-white' : 'bg-brown-dark/50 outline-brown-dark/50 text-white/50'}`}>
              2
            </div>
            <div className={`text-[22px] md:text-[30px] font-semibold leading-[28px] md:leading-[38px] mt-0.5 ${currentQuestionData.required === true ? 'text-brown-dark' : 'text-brown-dark/50'}`}>
              {translations.sidebar.section2}
            </div>
          </div>
        </div>
      </div>

      {/* Right Content Area */}

      <div className="flex-1 px-4 sm:px-8 md:px-12 lg:px-[100px] pb-4 md:pb-28 xl:pb-10 pt-6 md:pt-[100px] xl:pt-[9%] flex flex-col relative">
        <div className="flex flex-col justify-center w-full lg:max-w-[92%] question-content-wrapper">
          <div className={`question-content transition-all duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
            <h5 className="text-[20px] md:text-[26px] xl:text-[28px] text-brown-dark">{translations.labels.question} {convertToLocalNumerals(displayQuestion, language)}/{convertToLocalNumerals(totalQuestions, language)}</h5>
          </div>
          <div className={`question-content ${isTransitioning
            ? (transitionDirection === 'next' ? 'exiting-next' : 'exiting-prev')
            : (transitionDirection === 'next' ? 'entering-next' : 'entering-prev')
            }`}>
            <h2 className="text-[24px] md:text-[28px] lg:text-[36px] xl:text-[48px] leading-[120%] font-artifex font-semibold text-black m-0 mt-3 md:mt-5 whitespace-pre-line">
              {currentQuestionData[language].text}
            </h2>
          </div>

          <div className={`question-content mt-6 md:mt-10 ${isTransitioning
            ? (transitionDirection === 'next' ? 'exiting-next' : 'exiting-prev')
            : (transitionDirection === 'next' ? 'entering-next' : 'entering-prev')
            }`}>
            {currentQuestionData.type === 'text' && (
              <div className="relative" ref={inputRef}>
                <input
                  maxLength={maxLength}
                  type="text"
                  className="w-full border-b border-b-dark-shade1 bg-transparent pt-1 pb-2.5 text-[22px] sm:text-[26px] xl:text-[32px] text-dark-shade1 outline-none duration-200 placeholder:text-[#999] focus:border-b-brown-dark disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder={currentQuestionData[language].placeholder}
                  value={answers[displayQuestion] || ''}
                  onChange={(e) => {
                    setAnswers({ ...answers, [displayQuestion]: e.target.value });
                  }}
                  onFocus={(e) => {
                    // Prevent mobile keyboard from showing on touch devices
                    if (isTouchDevice) {
                      e.target.setAttribute('readonly', 'readonly');
                      handleInputFocus();
                    }
                  }}
                  onTouchStart={(e) => {
                    // Prevent mobile keyboard on touch devices
                    if (isTouchDevice) {
                      e.preventDefault();
                    }
                  }}
                  onBlur={handleInputBlur}
                  disabled={displayQuestion === 1 && isAnonymous}
                  readOnly={isTouchDevice}
                  inputMode={isTouchDevice ? "none" : undefined}
                  data-gramm="false"
                  data-gramm_editor="false"
                  data-enable-grammarly="false"
                />
                {displayQuestion === 1 && (
                  <div className="mt-10">
                    <label className="inline-flex items-center gap-3 md:gap-4 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={isAnonymous}
                        onChange={(e) => {
                          setIsAnonymous(e.target.checked);
                          if (e.target.checked) {
                            setAnswers({ ...answers, [displayQuestion]: '' });
                            setShowKeyboard(false);
                          } else {
                            console.log('Checkbox unchecked');
                          }
                        }}
                        className="appearance-none w-6 md:w-7 h-6 md:h-7 border-2 border-black bg-transparent rounded-md cursor-pointer flex-shrink-0 transition-all duration-200 checked:bg-brown-dark checked:border-brown-dark checked:bg-checkbox-checked checked:bg-[length:16px_auto] md:checked:bg-[length:20px_auto] checked:bg-center checked:bg-no-repeat relative"
                      />
                      <span className="text-[16px] md:text-[22px] xl:text-[28px] leading-[120%] text-dark-shade1">{translations.labels.anonymous}</span>
                    </label>
                  </div>
                )}
              </div>
            )}

            {currentQuestionData.type === 'textarea' && (
              <div className="relative" ref={inputRef}>
                <textarea
                  maxLength={maxLength}
                  className="w-full bg-dark-shade2 border border-dark-shade1 rounded-xl md:rounded-[20px] p-4 sm:p-5 text-[16px] md:text-[22px] xl:text-[28px] text-dark-shade1 outline-none duration-200 placeholder:text-[#999] focus:border-b-brown-dark h-[200px] sm:h-[252px] resize-none"
                  placeholder={currentQuestionData[language].placeholder}
                  value={answers[displayQuestion] || ''}
                  onChange={(e) => {
                    setAnswers({ ...answers, [displayQuestion]: e.target.value });
                  }}
                  onFocus={(e) => {
                    // Prevent mobile keyboard from showing on touch devices
                    if (isTouchDevice) {
                      e.target.setAttribute('readonly', 'readonly');
                      handleInputFocus();
                    }
                  }}
                  onTouchStart={(e) => {
                    // Prevent mobile keyboard on touch devices
                    if (isTouchDevice) {
                      e.preventDefault();
                    }
                  }}
                  onBlur={handleInputBlur}
                  readOnly={isTouchDevice}
                  inputMode={isTouchDevice ? "none" : undefined}
                  data-gramm="false"
                  data-gramm_editor="false"
                  data-enable-grammarly="false"
                />
              </div>
            )}

            {currentQuestionData.type === 'radio' && (
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 md:gap-6">
                {currentQuestionData.options.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleRadioChange(option.id)}
                    className={`rounded-lg md:rounded-xl border py-2 md:py-4 px-6 sm:px-7 text-[18px] md:text-[22px] xl:text-[28px] text-center transition-all duration-300 cursor-pointer min-h-[48px] md:min-[64px] lg:min-h-[80px] w-full sm:w-auto sm:min-w-[280px] md:min-w-[320px] active:scale-95 ${answers[displayQuestion] === option.id
                      ? 'bg-brown-dark text-white border-brown-dark opacity-100 shadow-lg shadow-brown-dark/40 ring-2 ring-brown-dark ring-offset-2 ring-offset-beige-light'
                      : 'bg-white/70 text-brown-dark border-brown-dark hover:border-brown-dark hover:bg-white/90'
                      }`}
                  >
                    {option[language].text}
                  </button>
                ))}
              </div>
            )}

            {currentQuestionData.type === 'checkbox' && (
              <div className="flex flex-col items-start gap-4 md:gap-6">
                {currentQuestionData.options.map((option) => (
                  <label key={option.id} className="flex items-center gap-3 md:gap-4 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      value={option.id}
                      checked={(answers[displayQuestion] || []).includes(option.id)}
                      onChange={() => handleCheckboxChange(option.id)}
                      className="appearance-none w-6 md:w-7 h-6 md:h-7 border-2 border-black bg-transparent rounded-md cursor-pointer flex-shrink-0 transition-all duration-200 checked:bg-brown-dark checked:border-brown-dark checked:bg-checkbox-checked checked:bg-[length:16px_auto] md:checked:bg-[length:20px_auto] checked:bg-center checked:bg-no-repeat relative"
                    />
                    <span className="text-[18px] sm:text-[22px] xl:text-[28px] leading-[120%] text-dark-shade1">{option[language].text}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-row justify-between items-center gap-4 sm:gap-0 mt-auto pt-10 sm:pt-14">
          {displayQuestion > 1 && (
            <button className="bg-transparent border-2 border-brown-primary font-forum text-brown-primary text-[18px] sm:text-[22px] xl:text-[28px] py-1 md:py-3 px-8 rounded-full cursor-pointer h-[48px] md:h-[56px] lg:h-[72px] w-auto sm:min-w-[220px] transition-all duration-300 hover:bg-brown-primary hover:text-white active:scale-95" onClick={handlePrev}>
              {translations.buttons.prev}</button>
          )}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 sm:gap-6 ml-auto w-auto">
            {displayQuestion < totalQuestions && (
              <button className="bg-brown-primary font-forum text-white text-[18px] sm:text-[22px] xl:text-[28px] py-1 md:py-3 px-8 rounded-full border-none cursor-pointer h-[48px] md:h-[56px] lg:h-[72px] w-auto sm:min-w-[220px] transition-all duration-300 hover:bg-brown-hover-alt active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleNext}
                disabled={(isSubmitting || !isCurrentQuestionValid())}>
                {translations.buttons.next}
              </button>
            )}
            {displayQuestion === totalQuestions && (
              <button className="bg-brown-primary font-forum text-white text-[18px] sm:text-[22px] xl:text-[28px] py-1 md:py-3 px-8 rounded-full border-none cursor-pointer h-[48px] md:h-[56px] lg:h-[72px] w-auto sm:min-w-[220px] transition-all duration-300 hover:bg-brown-hover-alt active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleSubmit}
                disabled={isSubmitting || !isCurrentQuestionValid()}>
                {isSubmitting
                  ? translations.buttons.submitting
                  : translations.buttons.submit
                }
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Keyboard - Show only for text/textarea questions on touch devices */}
      {isTextInput && showKeyboard && isTouchDevice && (
        <div ref={keyboardWrapperRef}>
          <MobileKeyboard
            onChange={handleKeyboardInput}
            inputName="feedback-input"
            inputValue={currentInputValue}
            isLastQuestion={displayQuestion === totalQuestions}
            isSubmitting={isSubmitting}
            isRequired={currentQuestionData.required || false}
            forceEnglishKeyboard={displayQuestion === 5}
            onReturn={() => {
              if (isCurrentQuestionValid()) {
                if (displayQuestion < totalQuestions) {
                  handleNext();
                } else {
                  handleSubmit();
                  setShowKeyboard(false);
                }
              } else {
                setShowKeyboard(false);
              }
            }}
          />
        </div>
      )}

      {/* Loader Overlay */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-beige-light/90 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-6">
            <div className="border-4 border-brown-primary/70 border-t border-t-brown-primary rounded-full w-14 h-14 animate-spin"></div>
            <p className="text-brown-dark text-[24px] xl:text-[28px] font-forum">
              {translations.buttons.submitting || 'Submitting...'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Feedback;

